import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { showNotificationToast, toastifySuccess } from '@/Components/Common/AlertMsg';

const signalRAPI = process.env.REACT_APP_SIGNALR_URL_KEY;
const APIurl = process.env.REACT_APP_DOMAIN_URL_KEY;

const getAgencyKey = (agencyIdOverride) =>
    String(agencyIdOverride ?? sessionStorage.getItem('AgencyID') ?? '');

const buildHubUrl = (agencyKey) =>
    `${signalRAPI}?AgencyID=${encodeURIComponent(agencyKey)}`;

const createHubConnection = (agencyKey) =>
    new HubConnectionBuilder()
        .withUrl(buildHubUrl(agencyKey), {
            accessTokenFactory: () => sessionStorage.getItem('PINID') ?? '',
        })
        .build();

let hubAgencyKey = null;

/** @type {import('@microsoft/signalr').HubConnection} */
export let connection = null;

const wirePersistentHandlers = (conn) => {
    conn.on('Logout', () => {
        toastifySuccess('Logout Successfully !!');
        console.log('SignalR connection stop initiated');
        conn.stop().catch((err) => console.error('Error disconnecting:', err));
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    });

    conn.on('ResetTimer', async () => {
        try {
            const token = sessionStorage.getItem('access_token');
            if (!token) return;
            const response = await fetch(`${APIurl}/Personnel/GetData_UpdatePersonnel`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ PINID: localStorage?.getItem('PINID') }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const res = await response.json();
            const parsedData = JSON.parse(res.data);
            const sessionTimeOut = parsedData.Table[0]?.SessionTimeOut;
            sessionStorage.setItem('SessionTimeOut', sessionTimeOut);
        } catch (error) {
            console.error('Error fetching session timeout data:', error);
        }
    });

    conn.on('ReceiveNotification', async (message) => {
        const token = sessionStorage.getItem('access_token');
        if (!token) return;
        const AgencyID = sessionStorage.getItem('AgencyID');
        try {
            const response = await fetch(`${APIurl}CAD/MasterPriority/GetDataDropDown_Priority`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ AgencyID }),
            });
            if (!response.ok) {
                console.error(`API error! Status: ${response.status}`);
                return;
            }

            const res = await response.json();
            const parsedData = res?.data ? JSON.parse(res.data) : null;

            if (!parsedData || !parsedData.Table) {
                console.warn('Invalid or missing priority data. Using default color.');
            }

            const getMarkerColor = (description) => {
                const priority = parsedData?.Table?.find((item) => item.Description === description);
                return priority?.BackColor || '#808080';
            };

            const color = getMarkerColor(message?.priority);
            showNotificationToast(message?.heading, message?.message, color);
        } catch (error) {
            console.error('Error handling notification:', error.message);
        }
    });

    conn.onclose(() => {
        console.warn('🔌 SignalR disconnected. Attempting reconnect...');
        if (conn.state === HubConnectionState.Disconnected) {
            console.log('SignalR connection start initiated');
            conn.start().catch(console.error);
        }
    });
};

const initConnectionForKey = (key) => {
    const next = createHubConnection(key);
    hubAgencyKey = key;
    connection = next;
    wirePersistentHandlers(connection);
};

initConnectionForKey(getAgencyKey());

/**
 * Rebuilds the hub connection when AgencyID (session or override) does not match the current hub URL.
 * Call and await this after AgencyID is written to sessionStorage and before connection.start().
 * @param {string|number|null|undefined} [agencyIdOverride] — e.g. login response AgencyID if you prefer not to rely on session alone
 */
export async function ensureSignalRHubMatchesSession(agencyIdOverride) {
    const key = getAgencyKey(agencyIdOverride);
    if (hubAgencyKey === key && connection) {
        return;
    }
    if (connection) {
        try {
            await connection.stop();
        } catch (e) {
            console.error('SignalR stop before hub reconfigure:', e);
        }
    }
    initConnectionForKey(key);
}

// Function to set up SignalR handlers with context functions
export const setupSignalRHandlers = (incidentRefetch, resourceRefetch) => {
    connection.off('MonitorIncidentsView');
    connection.off('MonitorResourceView');

    connection.on('MonitorIncidentsView', () => {
        incidentRefetch();
    });

    connection.on('MonitorResourceView', () => {
        resourceRefetch();
    });
};
